/**
 * Client-side OCR Service using Tesseract.js
 * Runs entirely in the browser - no server dependencies
 */

import Tesseract from 'tesseract.js';

// Cache the worker for better performance on subsequent scans
let cachedWorker = null;

/**
 * Initialize or get cached Tesseract worker
 */
const getWorker = async (onProgress) => {
  if (cachedWorker) {
    return cachedWorker;
  }

  const worker = await Tesseract.createWorker('eng', 1, {
    logger: (m) => {
      if (onProgress && m.status === 'recognizing text') {
        onProgress(Math.round(m.progress * 100));
      }
    },
  });

  cachedWorker = worker;
  return worker;
};

/**
 * Preprocess image for better OCR accuracy
 * Creates a canvas with enhanced contrast
 */
const preprocessImage = (imageFile) => {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = img.width;
        canvas.height = img.height;

        // Draw original image
        ctx.drawImage(img, 0, 0);

        // Get image data for processing
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Convert to grayscale and enhance contrast
        for (let i = 0; i < data.length; i += 4) {
          // Grayscale conversion
          const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

          // Contrast enhancement (simple threshold for text)
          const enhanced = gray > 128 ? 255 : gray < 80 ? 0 : gray * 1.5;

          data[i] = enhanced;     // R
          data[i + 1] = enhanced; // G
          data[i + 2] = enhanced; // B
          // Alpha stays the same
        }

        ctx.putImageData(imageData, 0, 0);

        // Return as blob
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png');
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(imageFile);
  });
};

/**
 * Perform OCR on an image file
 * @param {File} imageFile - The image file to process
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<{text: string, confidence: number}>}
 */
export const performOCR = async (imageFile, onProgress) => {
  try {
    // Report initial progress
    if (onProgress) onProgress(5);

    // Preprocess image for better accuracy
    const processedImage = await preprocessImage(imageFile);
    if (onProgress) onProgress(15);

    // Get or create worker
    const worker = await getWorker(onProgress);
    if (onProgress) onProgress(25);

    // Perform OCR
    const result = await worker.recognize(processedImage);

    if (onProgress) onProgress(100);

    return {
      text: result.data.text,
      confidence: result.data.confidence,
      words: result.data.words,
      lines: result.data.lines,
    };
  } catch (error) {
    console.error('OCR Error:', error);
    throw new Error('Failed to process image: ' + error.message);
  }
};

/**
 * Extract potential fund names from OCR text
 * Uses patterns common in Indian mutual fund statements
 */
export const extractFundNames = (ocrText) => {
  const lines = ocrText.split('\n').filter(line => line.trim().length > 10);
  const fundPatterns = [
    /(?:HDFC|ICICI|SBI|Axis|Kotak|Nippon|Tata|Aditya Birla|UTI|DSP|Mirae|PPFAS|Quant|Motilal|Edelweiss|Franklin|HSBC|Invesco|L&T|Mahindra|Canara|Union|Bandhan|Baroda|IDFC|IDBI|ITI|JM|LIC|PGIM|Principal|Quantum|Shriram|Sundaram)[\w\s\-&]+(?:Fund|Plan|Growth|Dividend|IDCW)/gi,
    /[\w\s\-&]+(?:Mutual Fund|MF)[\w\s\-&]*/gi,
    /[\w\s\-&]+(?:Direct|Regular)[\s\-]+(?:Growth|Dividend|IDCW)/gi,
  ];

  const foundFunds = new Set();

  // Try each pattern
  for (const pattern of fundPatterns) {
    const matches = ocrText.match(pattern);
    if (matches) {
      matches.forEach(match => {
        const cleaned = match.trim().replace(/\s+/g, ' ');
        if (cleaned.length > 15 && cleaned.length < 150) {
          foundFunds.add(cleaned);
        }
      });
    }
  }

  // Also check each line for fund-like content
  lines.forEach(line => {
    const cleaned = line.trim();
    // Lines containing common fund keywords
    if (
      (cleaned.includes('Fund') || cleaned.includes('Growth') || cleaned.includes('Direct')) &&
      cleaned.length > 20 &&
      cleaned.length < 150 &&
      !cleaned.match(/^\d/) && // Doesn't start with number
      !cleaned.includes('NAV') &&
      !cleaned.includes('Folio')
    ) {
      foundFunds.add(cleaned);
    }
  });

  return Array.from(foundFunds);
};

/**
 * Clean up worker when done (call on component unmount)
 */
export const terminateWorker = async () => {
  if (cachedWorker) {
    await cachedWorker.terminate();
    cachedWorker = null;
  }
};

export default {
  performOCR,
  extractFundNames,
  terminateWorker,
};
